import { useState, useRef, FormEvent } from 'react';
import SEO from '@/components/base/SEO';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = formRef.current;
    if (!form) return;

    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      setFormMessage('提交成功！感谢您的反馈。');
      return;
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d9oov6iftmtnfcfh0bgg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(text); } catch { parsed = null; }

      if (res.ok && parsed && (parsed as Record<string, string>).code === 'OK') {
        setFormStatus('success');
        setFormMessage('提交成功！感谢您的反馈，我们会尽快回复。');
        form.reset();
      } else {
        const errMsg =
          (parsed as Record<string, Record<string, string>>)?.meta?.message ||
          (parsed as Record<string, string>)?.message ||
          '提交失败，请稍后重试';
        setFormStatus('error');
        setFormMessage(errMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查连接后重试');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于樱花漫画网',
    description: '樱花漫画网品牌介绍、口碑展示与联系方式',
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '樱花漫画网',
    url: 'https://yinghuacomic.com',
    description: '全集漫画免费在线阅读平台',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'service@yinghuacomic.com',
      contactType: 'customer service',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://yinghuacomic.com' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://yinghuacomic.com/about' },
    ],
  };

  return (
    <>
      <SEO
        title="关于我们 - 樱花漫画网 | 品牌介绍与联系方式"
        description="樱花漫画网致力于为漫画爱好者提供全集漫画免费完整版下拉式在线阅读服务。了解我们的品牌故事、用户口碑与联系方式。韩漫日漫国漫全本漫画推荐平台。"
        keywords="樱花漫画网,关于我们,品牌介绍,联系方式,漫画网站"
        canonical="https://yinghuacomic.com/about"
        ogTitle="关于我们 - 樱花漫画网"
        ogDescription="了解樱花漫画网的品牌故事与联系方式"
        ogType="website"
        ogUrl="https://yinghuacomic.com/about"
        twitterCard="summary"
        jsonLd={[aboutSchema, orgSchema, breadcrumbSchema]}
      />

      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
        <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-6" aria-label="面包屑导航">
          <a href="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</a>
          <span>/</span>
          <span className="text-foreground-600">关于我们</span>
        </nav>

        <section className="mb-12">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
              <i className="ri-flower-line text-3xl text-primary-500"></i>
            </div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
              关于樱花漫画网
            </h1>
            <p className="text-sm text-foreground-500 leading-relaxed">
              樱花漫画网是一个专注于为漫画爱好者提供优质阅读体验的在线平台。我们致力于打造一个免费、完整、便捷的漫画阅读空间，让每一位热爱漫画的朋友都能在这里找到自己喜欢的作品。
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                <i className="ri-book-open-line text-xl text-primary-500"></i>
              </div>
              <h3 className="font-heading font-semibold text-foreground-800 mb-2">海量漫画资源</h3>
              <p className="text-sm text-foreground-500">
                涵盖韩漫、日漫、国漫三大类型，收录热门连载与经典完结作品，漫画全集免费阅读。
              </p>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
                <i className="ri-smartphone-line text-xl text-accent-500"></i>
              </div>
              <h3 className="font-heading font-semibold text-foreground-800 mb-2">下拉式阅读</h3>
              <p className="text-sm text-foreground-500">
                采用先进的下拉式阅读模式，流畅的滚动体验让您沉浸在漫画世界中，一次看个够。
              </p>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
                <i className="ri-refresh-line text-xl text-secondary-500"></i>
              </div>
              <h3 className="font-heading font-semibold text-foreground-800 mb-2">及时更新</h3>
              <p className="text-sm text-foreground-500">
                紧跟官方更新进度，热门漫画同步上架最新章节，让您第一时间阅读最新内容。
              </p>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-5 rounded-full bg-primary-500"></div>
            <h2 className="text-lg font-heading font-bold text-foreground-900">品牌故事</h2>
          </div>
          <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8">
            <p className="text-sm text-foreground-600 leading-relaxed mb-4">
              樱花漫画网创立于对漫画文化的热爱。我们深知每一位漫画迷在寻找好看的漫画时所面临的困扰——付费墙、不完整的章节、糟糕的阅读体验。因此，我们决定打造一个真正以读者为中心的漫画阅读平台。
            </p>
            <p className="text-sm text-foreground-600 leading-relaxed mb-4">
              从最初只有几十部漫画的小站，发展到现在拥有数百部漫画全集的平台，樱花漫画网始终秉持"免费、完整、优质"的理念。我们与众多漫画爱好者一起成长，不断完善阅读体验，优化分类导航，让每一位用户都能快速找到心仪的漫画全集。
            </p>
            <p className="text-sm text-foreground-600 leading-relaxed">
              如今，樱花漫画网已成为国内知名的免费漫画阅读平台之一，每天为数以万计的漫画迷提供韩漫、日漫、国漫的全本漫画推荐和在线阅读服务。我们相信，好的漫画应该被更多人看到，而我们的使命就是让这一切变得简单。
            </p>
          </div>
        </section>

        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-5 rounded-full bg-accent-500"></div>
            <h2 className="text-lg font-heading font-bold text-foreground-900">用户口碑</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                name: '漫画迷小王',
                text: '樱花漫画网真的是宝藏网站！全集漫画免费看，下拉式阅读太爽了，推荐了好几部好看的韩漫给朋友。',
                rating: 5,
              },
              {
                name: '二次元老粉',
                text: '追了咒术回战好几年，樱花漫画网更新速度和画质都很棒。日漫和国漫资源也很多，全本漫画一次看个够！',
                rating: 5,
              },
              {
                name: '深夜读者',
                text: '最喜欢下拉式阅读模式，晚上躺床上看漫画超舒服。漫画全集免费这一点真的太良心了。',
                rating: 5,
              },
              {
                name: '漫画推荐官',
                text: '作为漫画博主，我经常向粉丝推荐樱花漫画网。分类清晰，韩漫日漫国漫都有专区，找漫画很方便。',
                rating: 4,
              },
            ].map((review, i) => (
              <div key={i} className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                <div className="flex items-center gap-1 mb-2">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <div key={j} className="w-4 h-4 flex items-center justify-center">
                      <i
                        className={`text-xs ${
                          j < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'
                        }`}
                      ></i>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">{review.text}</p>
                <span className="text-xs text-foreground-400">— {review.name}</span>
              </div>
            ))}
          </div>
        </section>

        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-5 rounded-full bg-secondary-500"></div>
            <h2 className="text-lg font-heading font-bold text-foreground-900">联系我们</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100">
                  <i className="ri-mail-line text-lg text-primary-500"></i>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground-800 mb-1">电子邮箱</h4>
                  <p className="text-sm text-foreground-500">service@yinghuacomic.com</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100">
                  <i className="ri-global-line text-lg text-accent-500"></i>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground-800 mb-1">官方网站</h4>
                  <p className="text-sm text-foreground-500">yinghuacomic.com</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-secondary-100">
                  <i className="ri-time-line text-lg text-secondary-500"></i>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground-800 mb-1">工作时间</h4>
                  <p className="text-sm text-foreground-500">周一至周五 9:00 - 18:00</p>
                </div>
              </div>
            </div>

            <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
              <h3 className="font-heading font-semibold text-foreground-800 mb-4">发送反馈</h3>
              <form
                ref={formRef}
                onSubmit={handleSubmit}
                className="space-y-4"
                data-readdy-form
              >
                <div>
                  <input
                    type="text"
                    name="name"
                    placeholder="您的姓名"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    name="email"
                    placeholder="您的邮箱"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                  />
                </div>
                <div>
                  <textarea
                    name="message"
                    placeholder="请描述您的意见或建议（500字以内）"
                    rows={4}
                    maxLength={500}
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                  />
                </div>
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />
                <button
                  type="submit"
                  className="w-full px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  提交反馈
                </button>
                {formStatus !== 'idle' && (
                  <p
                    className={`text-xs text-center ${
                      formStatus === 'success' ? 'text-secondary-600' : 'text-primary-600'
                    }`}
                  >
                    {formMessage}
                  </p>
                )}
              </form>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}